// Copyright (c) Mysten Labs, Inc.
// Modifications Copyright (c) 2024 IOTA Stiftung
// SPDX-License-Identifier: Apache-2.0

/* tslint:disable */
/* eslint-disable */
// @ts-nocheck
/**
 * Ampli - A strong typed wrapper for your Analytics
 *
 * This file is generated by Amplitude.
 * To update run 'ampli pull web'
 *
 * Required dependencies: @amplitude/analytics-browser@^1.3.0
 * Tracking Plan Version: 1
 * Build: 1.0.0
 * Runtime: browser:typescript-ampli-v2
 *
 * [View Tracking Plan](https://data.eu.amplitude.com/iota-foundation/IOTA%20Explorer/events/main/latest)
 *
 * [Full Setup Instructions](https://data.eu.amplitude.com/iota-foundation/IOTA%20Explorer/implementation/web)
 */

import * as amplitude from '@amplitude/analytics-browser';

export type Environment = 'iotaexplorer';

export const ApiKey: Record<Environment, string> = {
    iotaexplorer: '896b9073219c06800d9bf0aecf1b6f80',
};

/**
 * Default Amplitude configuration options. Contains tracking plan information.
 */
export const DefaultConfiguration: BrowserOptions = {
    plan: {
        version: '1',
        branch: 'main',
        source: 'web',
        versionId: '500c0ddf-5139-4575-8884-b644a083e933',
    },
    ...{
        ingestionMetadata: {
            sourceName: 'browser-typescript-ampli',
            sourceVersion: '2.0.0',
        },
    },
    serverZone: amplitude.Types.ServerZone.EU,
};

export interface LoadOptionsBase {
    disabled?: boolean;
}

export type LoadOptionsWithEnvironment = LoadOptionsBase & {
    environment: Environment;
    client?: { configuration?: BrowserOptions };
};
export type LoadOptionsWithApiKey = LoadOptionsBase & {
    client: { apiKey: string; configuration?: BrowserOptions };
};
export type LoadOptionsWithClientInstance = LoadOptionsBase & {
    client: { instance: BrowserClient };
};

export type LoadOptions =
    | LoadOptionsWithEnvironment
    | LoadOptionsWithApiKey
    | LoadOptionsWithClientInstance;

export interface ClickedCurrentEpochCardProperties {
    /**
     * | Rule | Value |
     * |---|---|
     * | Type | number |
     */
    epoch?: number;
}

export interface ClickedSearchResultProperties {
    /**
     * | Rule | Value |
     * |---|---|
     * | Regex |  |
     */
    searchCategory?: string;
    /**
     * | Rule | Value |
     * |---|---|
     * | Regex |  |
     */
    searchQuery?: string;
}

export interface ClickedValidatorRowProperties {
    /**
     * | Rule | Value |
     * |---|---|
     * | Regex |  |
     */
    sourceFlow?: string;
    /**
     * | Rule | Value |
     * |---|---|
     * | Regex |  |
     */
    validatorAddress?: string;
    /**
     * | Rule | Value |
     * |---|---|
     * | Regex |  |
     */
    validatorName?: string;
}

export interface CompletedSearchProperties {
    /**
     * | Rule | Value |
     * |---|---|
     * | Regex |  |
     */
    searchQuery?: string;
}

export interface OpenedIotaExplorerProperties {
    activeNetwork?: string;
    pageDomain?: string;
    pagePath?: string;
    pageUrl?: string;
}

export interface SwitchedNetworkProperties {
    /**
     * | Rule | Value |
     * |---|---|
     * | Regex |  |
     */
    toNetwork?: string;
}

export class ClickedCurrentEpochCard implements BaseEvent {
    event_type = 'clicked current epoch card';

    constructor(public event_properties?: ClickedCurrentEpochCardProperties) {
        this.event_properties = event_properties;
    }
}

export class ClickedSearchResult implements BaseEvent {
    event_type = 'clicked search result';

    constructor(public event_properties?: ClickedSearchResultProperties) {
        this.event_properties = event_properties;
    }
}

export class ClickedValidatorRow implements BaseEvent {
    event_type = 'clicked validator row';

    constructor(public event_properties?: ClickedValidatorRowProperties) {
        this.event_properties = event_properties;
    }
}

export class CompletedSearch implements BaseEvent {
    event_type = 'completed search';

    constructor(public event_properties?: CompletedSearchProperties) {
        this.event_properties = event_properties;
    }
}

export class OpenedIotaExplorer implements BaseEvent {
    event_type = 'opened iota explorer';

    constructor(public event_properties?: OpenedIotaExplorerProperties) {
        this.event_properties = event_properties;
    }
}

export class SwitchedNetwork implements BaseEvent {
    event_type = 'switched network';

    constructor(public event_properties?: SwitchedNetworkProperties) {
        this.event_properties = event_properties;
    }
}

export type PromiseResult<T> = { promise: Promise<T | void> };

const getVoidPromiseResult = () => ({ promise: Promise.resolve() });

// prettier-ignore
export class Ampli {
  private disabled: boolean = false;
  private amplitude?: BrowserClient;

  get client(): BrowserClient {
    this.isInitializedAndEnabled();
    return this.amplitude!;
  }

  get isLoaded(): boolean {
    return this.amplitude != null;
  }

  private isInitializedAndEnabled(): boolean {
    if (!this.amplitude) {
      console.error('ERROR: Ampli is not yet initialized. Have you called ampli.load() on app start?');
      return false;
    }
    return !this.disabled;
  }

  /**
   * Initialize the Ampli SDK. Call once when your application starts.
   *
   * @param options Configuration options to initialize the Ampli SDK with.
   */
  load(options: LoadOptions): PromiseResult<void> {
    this.disabled = options.disabled ?? false;

    if (this.amplitude) {
      console.warn('WARNING: Ampli is already initialized. Ampli.load() should be called once at application startup.');
      return getVoidPromiseResult();
    }

    let apiKey: string | null = null;
    if (options.client && 'apiKey' in options.client) {
      apiKey = options.client.apiKey;
    } else if ('environment' in options) {
      apiKey = ApiKey[options.environment];
    }

    if (options.client && 'instance' in options.client) {
      this.amplitude = options.client.instance;
    } else if (apiKey) {
      this.amplitude = amplitude.createInstance();
      const configuration = (options.client && 'configuration' in options.client) ? options.client.configuration : {};
      return this.amplitude.init(apiKey, undefined, { ...DefaultConfiguration, ...configuration });
    } else {
      console.error("ERROR: ampli.load() requires 'environment', 'client.apiKey', or 'client.instance'");
    }

    return getVoidPromiseResult();
  }

  /**
   * Identify a user and set user properties.
   *
   * @param userId The user's id.
   * @param options Optional event options.
   */
  identify(
    userId: string | undefined,
    options?: EventOptions,
  ): PromiseResult<Result> {
    if (!this.isInitializedAndEnabled()) {
      return getVoidPromiseResult();
    }

    if (userId) {
      options = {...options,  user_id: userId};
    }

    const amplitudeIdentify = new amplitude.Identify();
    return this.amplitude!.identify(
      amplitudeIdentify,
      options,
    );
  }

 /**
  * Flush the event.
  */
  flush() : PromiseResult<Result> {
    if (!this.isInitializedAndEnabled()) {
      return getVoidPromiseResult();
    }

    return this.amplitude!.flush();
  }

  /**
   * Track event
   *
   * @param event The event to track.
   * @param options Optional event options.
   */
  track(event: Event, options?: EventOptions): PromiseResult<Result> {
    if (!this.isInitializedAndEnabled()) {
      return getVoidPromiseResult();
    }

    return this.amplitude!.track(event, undefined, options);
  }

  /**
   * clicked current epoch card
   *
   * [View in Tracking Plan](https://data.eu.amplitude.com/iota-foundation/IOTA%20Explorer/events/main/latest/clicked%20current%20epoch%20card)
   *
   * Event has no description in tracking plan.
   *
   * @param properties The event's properties (e.g. epoch)
   * @param options Amplitude event options.
   */
  clickedCurrentEpochCard(
    properties?: ClickedCurrentEpochCardProperties,
    options?: EventOptions,
  ) {
    return this.track(new ClickedCurrentEpochCard(properties), options);
  }

  /**
   * clicked search result
   *
   * [View in Tracking Plan](https://data.eu.amplitude.com/iota-foundation/IOTA%20Explorer/events/main/latest/clicked%20search%20result)
   *
   * Event has no description in tracking plan.
   *
   * @param properties The event's properties (e.g. searchCategory)
   * @param options Amplitude event options.
   */
  clickedSearchResult(
    properties?: ClickedSearchResultProperties,
    options?: EventOptions,
  ) {
    return this.track(new ClickedSearchResult(properties), options);
  }

  /**
   * clicked validator row
   *
   * [View in Tracking Plan](https://data.eu.amplitude.com/iota-foundation/IOTA%20Explorer/events/main/latest/clicked%20validator%20row)
   *
   * Event has no description in tracking plan.
   *
   * @param properties The event's properties (e.g. sourceFlow)
   * @param options Amplitude event options.
   */
  clickedValidatorRow(
    properties?: ClickedValidatorRowProperties,
    options?: EventOptions,
  ) {
    return this.track(new ClickedValidatorRow(properties), options);
  }

  /**
   * completed search
   *
   * [View in Tracking Plan](https://data.eu.amplitude.com/iota-foundation/IOTA%20Explorer/events/main/latest/completed%20search)
   *
   * Event has no description in tracking plan.
   *
   * @param properties The event's properties (e.g. searchQuery)
   * @param options Amplitude event options.
   */
  completedSearch(
    properties?: CompletedSearchProperties,
    options?: EventOptions,
  ) {
    return this.track(new CompletedSearch(properties), options);
  }

  /**
   * opened iota explorer
   *
   * [View in Tracking Plan](https://data.eu.amplitude.com/iota-foundation/IOTA%20Explorer/events/main/latest/opened%20iota%20explorer)
   *
   * Event has no description in tracking plan.
   *
   * @param properties The event's properties (e.g. activeNetwork)
   * @param options Amplitude event options.
   */
  openedIotaExplorer(
    properties?: OpenedIotaExplorerProperties,
    options?: EventOptions,
  ) {
    return this.track(new OpenedIotaExplorer(properties), options);
  }

  /**
   * switched network
   *
   * [View in Tracking Plan](https://data.eu.amplitude.com/iota-foundation/IOTA%20Explorer/events/main/latest/switched%20network)
   *
   * Event has no description in tracking plan.
   *
   * @param properties The event's properties (e.g. toNetwork)
   * @param options Amplitude event options.
   */
  switchedNetwork(
    properties?: SwitchedNetworkProperties,
    options?: EventOptions,
  ) {
    return this.track(new SwitchedNetwork(properties), options);
  }
}

export const ampli = new Ampli();

// BASE TYPES
type BrowserOptions = amplitude.Types.BrowserOptions;

export type BrowserClient = amplitude.Types.BrowserClient;
export type BaseEvent = amplitude.Types.BaseEvent;
export type IdentifyEvent = amplitude.Types.IdentifyEvent;
export type GroupEvent = amplitude.Types.GroupIdentifyEvent;
export type Event = amplitude.Types.Event;
export type EventOptions = amplitude.Types.EventOptions;
export type Result = amplitude.Types.Result;
